// CHANGE v2 §12: cartão Início (próximo passo + progresso) — GuidedFlow.dc.
import { useState } from 'react';
import { ArrowRight, CaretRight, Check, DotsThree, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '../ui/Button';
import { guidedFlowAPI } from '../../data/auth';
import { useGuidedFlowQuery } from '../../features/guidedFlow/queries';
import { useSnapshotsTimelineQuery } from '../../features/snapshots/queries';
import { useAuth } from '../../store/AuthContext';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import pt from '../../locales/pt';
import {
  apiErrorMessage,
  countCompletedGuidedSteps,
  filterVisibleGuidedSteps,
  formatGuidedFlowTemplate,
  isGuidedFlowFinished,
  resolveGuidedWizardStep,
} from '../../utils/guidedFlowUtils';
import { cn } from '../../utils/cn';

const DISMISS_FLAG = 'guided_flow_dismissed';

export function useGuidedFlowDashboardState() {
  const { data: state } = useGuidedFlowQuery();
  const { profile } = useAuth();
  const { currentCycle } = useSelfieCycle();
  const { data: patrimonioTimeline = [] } = useSnapshotsTimelineQuery();
  const hasSnapshots = patrimonioTimeline.length > 0;
  const stepCompleted = state?.step_completed || {};
  const dismissed = (profile?.seen_wizards || []).includes(DISMISS_FLAG);

  const visibilityOptions = {
    hasSnapshots,
    stepCompleted,
    selfieDayOfMonth: profile?.selfie_day_of_month,
    selfieDayConfigured:
      (profile?.seen_wizards || []).includes('selfie_day_setup') ||
      Boolean(stepCompleted.g1_selfie_day),
    hasActiveDebts: profile?.has_active_debts,
    currentCycle,
  };

  const finished = !state || isGuidedFlowFinished(state, visibilityOptions);

  return {
    state,
    finished,
    dismissed,
    visibilityOptions,
    stepCompleted,
  };
}

/**
 * Cartão do dashboard. O modal ("Ver a jornada toda") vive em `Dashboard.jsx`
 * — não aqui — porque este cartão desmonta assim que `finished` fica `true`
 * (troca para `DashboardNetWorthHero`/`GoalFocusCard`), o que cortaria a
 * celebração a meio se o modal fosse filho deste componente.
 */
export function DashboardGuidedFlowHero({ className, onOpenWizard }) {
  const [dismissing, setDismissing] = useState(false);
  const { refreshMe } = useAuth();
  const { state, finished, dismissed, visibilityOptions, stepCompleted } =
    useGuidedFlowDashboardState();

  if (!state || dismissed || finished) return null;

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const res = await guidedFlowAPI.markWizardSeen(DISMISS_FLAG);
      if (!res.success) {
        toast.error(apiErrorMessage(res.error, 'Não foi possível esconder este cartão.'));
        return;
      }
      await refreshMe();
    } finally {
      setDismissing(false);
    }
  };

  const visibleSteps = filterVisibleGuidedSteps(state.order, visibilityOptions);
  const activeStep = resolveGuidedWizardStep(state, visibilityOptions);
  const doneCount = countCompletedGuidedSteps(
    state.order,
    stepCompleted,
    { ...visibilityOptions, skipped: state.skipped || [] },
  );
  const totalVisible = visibleSteps.length;
  const stepCopy = activeStep ? pt.dashboard.guidedFlow.steps[activeStep] : null;
  const copy = pt.dashboard.guidedFlow;

  const currentIndex = activeStep ? visibleSteps.indexOf(activeStep) : -1;
  const upcomingSteps = currentIndex >= 0 ? visibleSteps.slice(currentIndex + 1) : [];
  const shownUpcoming = upcomingSteps.slice(0, 2);
  const moreCount = upcomingSteps.length - shownUpcoming.length;

  const progressHint = formatGuidedFlowTemplate(copy.heroProgressHint, {
    done: doneCount,
    total: totalVisible,
  });
  const doneStepsLabel =
    doneCount === 1
      ? copy.doneStepLabel
      : formatGuidedFlowTemplate(copy.doneStepsLabel, { count: doneCount });

  return (
      <section
        className={cn(
          'flex flex-col gap-space-4 rounded-ds-lg border border-sage-border bg-card p-space-4',
          className,
        )}
      >
        <div className="flex items-center gap-space-3">
          <GuidedFlowRing
            percent={totalVisible > 0 ? (doneCount / totalVisible) * 100 : 0}
            label={`${doneCount}/${totalVisible}`}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-space-1">
            <p className="font-display text-type-title-card font-semibold text-card-foreground">
              {copy.heroBadge}
            </p>
            <p className="text-type-label font-normal text-muted-foreground">{progressHint}</p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={dismissing}
            aria-label={copy.dismissLabel}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-ds-full bg-muted text-muted-foreground transition hover:bg-stone-200 disabled:opacity-50 dark:hover:bg-stone-700"
          >
            <X className="h-3.5 w-3.5" weight="bold" aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-space-2">
          {doneCount > 0 ? (
            <div className="flex items-center gap-space-3">
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-ds-full bg-sage-tint">
                <Check className="h-3 w-3 text-sage-on-tint" weight="bold" aria-hidden />
              </span>
              <span className="text-type-body-sm font-medium text-muted-foreground">
                {doneStepsLabel}
              </span>
            </div>
          ) : null}

          {stepCopy ? (
            <div className="flex flex-col gap-space-2 rounded-ds-md bg-sage-tint p-space-3">
              <div className="flex items-center gap-space-2">
                <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-ds-full bg-gradient-to-br from-sage-500 to-sage-600 shadow-[0_3px_10px] shadow-sage-glow">
                  <span className="text-type-chip font-bold text-sage-50">
                    {currentIndex + 1}
                  </span>
                </span>
                <span className="type-section text-sage-on-tint tracking-[0.08em]">
                  {copy.nextStepLabel}
                </span>
              </div>
              <p className="font-display text-type-title-card font-semibold text-card-foreground">
                {stepCopy.title}
              </p>
              <p className="text-type-body-sm font-normal text-muted-foreground">
                {stepCopy.subtitle}
              </p>
              <Button type="button" className="w-full" onClick={onOpenWizard}>
                {stepCopy.cta}
                <ArrowRight className="h-[18px] w-[18px]" weight="bold" aria-hidden />
              </Button>
            </div>
          ) : null}

          {shownUpcoming.map((stepKey, idx) => (
            <div key={stepKey} className="flex items-center gap-space-3">
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-ds-full border border-input-border">
                <span className="text-type-chip font-bold text-muted-foreground">
                  {currentIndex + 2 + idx}
                </span>
              </span>
              <span className="text-type-body-sm font-medium text-muted-foreground">
                {pt.dashboard.guidedFlow.steps[stepKey]?.title}
              </span>
            </div>
          ))}

          {moreCount > 0 ? (
            <div className="flex items-center gap-space-3">
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center">
                <DotsThree className="h-3.5 w-3.5 text-muted-foreground" weight="bold" aria-hidden />
              </span>
              <span className="text-type-body-sm font-normal text-muted-foreground">
                {formatGuidedFlowTemplate(copy.moreStepsPending, { count: moreCount })}
              </span>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onOpenWizard}
          className="flex items-center justify-center gap-space-1 text-type-label font-semibold text-sage-on-tint hover:underline"
        >
          {copy.viewJourneyLink}
          <CaretRight className="h-3.5 w-3.5" weight="bold" aria-hidden />
        </button>
      </section>
  );
}

function GuidedFlowRing({ percent, label }) {
  const deg = Math.round(Math.max(0, Math.min(100, percent)) * 3.6);
  return (
    <div
      className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
      style={{ background: `conic-gradient(#7C9E6F ${deg}deg, rgb(var(--track)) ${deg}deg)` }}
      aria-hidden
    >
      <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-card">
        <span className="text-type-label font-bold text-sage-on-tint">{label}</span>
      </div>
    </div>
  );
}

export default DashboardGuidedFlowHero;
